import { useState } from 'react';
import { arrearsApi } from 'Feature/Arrears/components/ArrearsInquiryPage/api/ArrearsApi';
import { Arrears, PagedArrears } from 'Feature/Arrears/components/ArrearsInquiryPage/core/Arrears';
import { ValidLoanNumber } from 'Feature/Arrears/components/ArrearsInquiryPage/core/LoanNumber';
import { Ordering } from 'Feature/Arrears/components/ArrearsInquiryPage/core/Ordering';
import { Paging } from 'Feature/Arrears/components/ArrearsInquiryPage/core/Paging';
import { useLanguage } from 'Feature/Arrears/components/ArrearsInquiryPage/core/useLanguage';
import { useAuthenticationContext } from 'Foundation/Authentication';
import { getLatestArrears } from 'Feature/Arrears/components/ArrearsInquiryPage/core/getLatestArrears';
import { config } from '../../../../../config';

export const useArrears = () => {
  const { language } = useLanguage();
  const authenticationContext = useAuthenticationContext();
  const api = arrearsApi(authenticationContext, config.arrearsApi.urls.arrears, language);

  const [arrears, setArrears] = useState<PagedArrears | undefined>(undefined);
  const [ordering, setOrdering] = useState<Ordering>(Ordering.defaultValue);
  const [loadingArrears, setLoadingArrears] = useState<boolean>(false);
  const [reloadingArrears, setReloadingArrears] = useState<boolean>(false);
  const [arrearsError, setArrearsError] = useState<string | undefined>(undefined);

  const [latestArrears, setLatestArrears] = useState<Arrears | undefined>(undefined);

  const fetchArrears = async (loanNumber: ValidLoanNumber) => {
    setLoadingArrears(true);
    await fetch(loanNumber, Paging.defaultValue, Ordering.defaultValue, true);
    setLoadingArrears(false);
  };

  const clearArrears = () => {
    setArrears(undefined);
    setLatestArrears(undefined);
    setArrearsError(undefined);
    setOrdering(Ordering.defaultValue);
  };

  const changePaging = async (loanNumber: ValidLoanNumber, newPaging: Paging) => {
    setReloadingArrears(true);
    await fetch(loanNumber, newPaging, ordering);
    setReloadingArrears(false);
  };

  const changeOrdering = async (loanNumber: ValidLoanNumber, newOrdering: Ordering) => {
    setReloadingArrears(true);
    setOrdering(newOrdering);
    await fetch(loanNumber, Paging.defaultValue, newOrdering);
    setReloadingArrears(false);
  };

  const updateArrears = (modifiedArrears: Arrears) => {
    if (!arrears) {
      return;
    }

    const updatedArrears = {
      paging: arrears.paging,
      data: arrears.data.map((item) => (item.id === modifiedArrears.id ? modifiedArrears : item))
    };

    setArrears(updatedArrears);
    setLatestArrears(getLatestArrears(updatedArrears.data));
  };

  const fetch = async (
    loanNumber: ValidLoanNumber,
    paging: Paging,
    ordering: Ordering,
    updateLatestArrears: boolean = false
  ) => {
    const result = await api.get(loanNumber, paging, ordering);
    if (result.isFailure) {
      setArrearsError(result.error);
      setArrears(undefined);
      setLatestArrears(undefined);
    } else {
      setArrearsError(undefined);
      setArrears(result.value);
      if (updateLatestArrears) {
        setLatestArrears(getLatestArrears(result.value.data));
      }
    }
  };

  return {
    arrears,
    latestArrears,
    fetchArrearsError: arrearsError,
    loadingArrears,
    fetchArrears,
    clearArrears,
    reloadingArrears,
    changePaging,
    changeOrdering,
    updateArrears
  };
};
